import { Component, defineElement } from '@theme/component';
import { ProductFormState } from '@theme/product-form-state';
import { resolveProductAccess } from '@theme/product-access-state';
import { ProductNotifyDialogs } from '@theme/product-notify-dialog';

const setText = (element, value) => {
  if (element && element.textContent !== String(value ?? '')) element.textContent = String(value ?? '');
};
const setHtml = (element, value) => {
  if (element && element.innerHTML !== String(value ?? '')) element.innerHTML = String(value ?? '');
};
const show = (element, visible) => {
  if (element) element.style.display = visible ? '' : 'none';
};

class YiseProductForm extends Component {
  owned(selector) {
    return [...this.querySelectorAll(selector)].filter((node) => node.closest('yise-product-form') === this);
  }

  one(selector) {
    return this.owned(selector)[0] || null;
  }

  get currentVariant() {
    return this.state?.current_variant || null;
  }

  ready() {
    if (this.initialized) return;
    if (!window.YISETemporarySalePrice && !this.pricingReady && document.readyState !== 'complete') {
      const initialize = () => {
        this.pricingReady = true;
        this.ready();
      };
      this.listen(document, 'DOMContentLoaded', initialize, { once: true });
      this.listen(window, 'load', initialize, { once: true });
      return;
    }
    const configNode = this.one('script[data-product-form-config]');
    try { this.config = JSON.parse(configNode?.textContent || ''); } catch {
      this.waitForConfig();
      return;
    }
    this.configObserver?.disconnect();
    this.configObserver = null;
    this.initialized = true;
    this.state = new ProductFormState(this.config, {
      pricingRuntime: window.YISETemporarySalePrice,
      bodyAttribute: (name) => document.body.getAttribute(name),
    });
    this.initialQuery = new URLSearchParams(location.search);
    this.isPreview = this.initialQuery.get('preview')?.toLowerCase() === 'true'
      || location.hostname.includes('shopifypreview.com');
    this.form = this.one('form[action*="/cart/add"]');
    this.state.selectedSellingPlanId = this.form?.querySelector('input[name="selling_plan"]')?.value || null;
    this.dialogs = new ProductNotifyDialogs(this, {
      strings: this.config.strings,
      productTitle: this.config.productTitle,
      getVariant: () => this.currentVariant,
      getAccess: () => this.access,
    });
    this.listen(this, 'change', (event) => this.handleChange(event));
    this.listen(this, 'click', (event) => this.handleClick(event));
    this.listen(this, 'keydown', (event) => this.handleKeydown(event));
    this.listen(this, 'yise:purchase-option-change', (event) => this.handlePurchaseChange(event));
    this.listen(this, 'yise:purchase-options-ready', (event) => {
      if (event.target.closest('yise-product-form') !== this) return;
      this.syncInitialPurchaseOptions();
      this.releasePendingControls();
    });
    this.listen(this, 'input', (event) => this.handlePlanInput(event));
    this.listen(this, 'change', (event) => this.handlePlanInput(event));
    if (this.config.history && this.config.defaultVariantId && !this.initialQuery.has('variant') && !this.dataset.bundleInitialVariantId) {
      this.updateHistory(this.currentVariant);
      if (this.owned('.media-gallery_swiper-container-wrapper').length > 1) this.syncMedia(this.currentVariant);
    }
    if (this.shouldRedirect()) return;
    this.applyVariant(this.currentVariant, { initial: true, updateHistory: false });
    this.openIntentDialog();
    this.reconcileVariantVisibility();
    this.syncOptions(this.currentVariant);
    this.syncInitialPurchaseOptions();
    this.observeSellingPlanInput();
    this.formControlsReady = true;
    this.releasePendingControls();
    this.dispatchEvent(new CustomEvent('yise:product-form-ready'));
    if (this.config.history) {
      const token = {};
      this.galleryReadyToken = token;
      const startGallery = async () => {
        if (!this.isConnected || this.galleryReadyToken !== token) return;
        try {
          const { createPdpGallery } = await import('@theme/pdp-gallery');
          if (this.isConnected && this.galleryReadyToken === token) this.galleryCleanup = createPdpGallery(this);
        } catch (error) {
          if (this.isConnected && this.galleryReadyToken === token) console.error('[YISE product gallery]', error);
        }
      };
      if (window.yiseThemeScriptsReady) window.yiseThemeScriptsReady.then(startGallery, () => {});
      else this.listen(window, 'yise:theme-scripts:ready', startGallery, { once: true });
    }
  }

  waitForConfig() {
    if (this.configObserver || typeof MutationObserver === 'undefined') return;
    this.configObserver = new MutationObserver(() => this.ready());
    this.configObserver.observe(this, { childList: true, subtree: true, characterData: true });
  }

  teardown() {
    this.initialized = false;
    this.formControlsReady = false;
    this.owned('[data-variant-option]').forEach((control) => {
      control.disabled = true;
      control.setAttribute('data-pending-product-control', '');
    });
    this.configObserver?.disconnect();
    this.configObserver = null;
    this.planObserver?.disconnect();
    this.dialogs?.teardown();
    this.galleryCleanup?.();
    this.galleryReadyToken = null;
    this.galleryTimers?.forEach(clearTimeout);
    this.galleryTimers?.clear();
    this.state = null;
  }

  shouldRedirect() {
    if (!this.config.history || this.state.variants.length <= 1) return false;
    const query = this.initialQuery || new URLSearchParams(location.search);
    if (['OutOfStock', 'waitlist', 'waitlist_signup'].some((name) => query.get(name) === 'true')) return false;
    const requested = this.state.variants.find((variant) => variant.id === query.get('variant'));
    if (!requested || requested.available || requested.wl_active) return false;
    const next = this.state.variants.find((variant) => variant.available);
    if (!next) return false;
    location.href = `/products/${this.config.productHandle}?variant=${next.id}`;
    return true;
  }

  syncInitialPurchaseOptions() {
    const purchase = this.one('yise-purchase-options');
    if (!this.isConnected || !this.state || !purchase?.initialized || typeof purchase.syncVariant !== 'function') return;
    purchase.syncVariant(this.currentVariant);
    this.state.selectedSellingPlanId = purchase.selectedSellingPlanId || null;
    if (this.state.selectedSellingPlanId) this.state.updatePriceForSellingPlan();
    this.updateCartTotal();
    this.updateExcludedVariantBodyAttribute();
    this.renderState();
    this.updateCartFormData();
  }

  observeSellingPlanInput() {
    if (!this.form || typeof MutationObserver === 'undefined') return;
    this.planObserver = new MutationObserver(() => {
      const input = this.form.querySelector('input[name="selling_plan"]');
      if (input) this.handlePlanInput({ target: input });
    });
    this.planObserver.observe(this.form, { subtree: true, childList: true, attributes: true, attributeFilter: ['value'] });
  }

  handleChange(event) {
    const input = event.target.closest?.('[data-variant-option]');
    if (!input || input.closest('yise-product-form') !== this) return;
    const chosen = new Map();
    this.owned('[data-variant-option]').forEach((control) => {
      if (control.type !== 'radio' || control.checked) chosen.set(Number(control.dataset.optionPosition), control.value);
    });
    const variant = this.state.variants.find((candidate) => [1, 2, 3].every((position) => {
      const value = candidate[`option${position}`];
      return !value || value === chosen.get(position);
    }));
    if (!variant) return;
    this.selectVariantById(variant.id, { announce: true });
  }

  syncCartVariantById(id) {
    const variant = this.state?.variants.find((candidate) => String(candidate.id) === String(id));
    if (!variant) return false;
    this.state.current_variant_id = String(variant.id);
    this.state.applyVariantPricing(variant);
    this.syncCartFormVariant(variant);
    this.updateCartFormData();
    return true;
  }

  selectVariantById(id, { announce = false } = {}) {
    const variant = this.state?.variants.find((candidate) => String(candidate.id) === String(id));
    if (!variant) return false;
    const schedule = Date.parse(variant.schedule_visibility);
    const hidden = !this.isPreview && ((Number.isFinite(schedule) && Date.now() < schedule) || (!variant.available && variant.hide_unavailable_option));
    if (hidden) {
      this.applyVariant(variant, { updateHistory: false });
      this.reconcileVariantVisibility();
      if (this.config.history) this.updateHistory(variant);
    } else this.applyVariant(variant);
    if (announce) this.announceVariantChange(this.currentVariant);
    return true;
  }

  applyVariant(variant, { initial = false, updateHistory = true } = {}) {
    if (!variant) return;
    if (!initial) this.syncWishlist(variant);
    this.state.current_variant_id = String(variant.id);
    this.access = resolveProductAccess(variant, {
      customerLoggedIn: this.config.customerLoggedIn,
      location,
      cookie: document.cookie,
      writeCookie: (value) => { document.cookie = value; },
    });
    this.dataset.currentVariantId = String(variant.id);
    this.state.applyVariantPricing(variant);
    const purchase = this.one('yise-purchase-options');
    if (!initial && purchase?.syncVariant) {
      purchase.selectedSellingPlanId = this.state.selectedSellingPlanId;
      purchase.syncVariant(variant);
      this.state.selectedSellingPlanId = purchase.selectedSellingPlanId;
    }
    if (!initial) {
      this.syncMedia(variant);
      if (updateHistory && this.config.history) this.updateHistory(variant);
    }
    if (!initial || this.state.selectedSellingPlanId) this.state.updatePriceForSellingPlan();
    this.updateCartTotal();
    this.updateExcludedVariantBodyAttribute();
    this.syncOptions(variant);
    this.renderState();
    this.updateCartFormData();
    this.dialogs?.updateVariant();
    if (!initial) {
      this.dispatchEvent(new CustomEvent('yise:product-variant-change', {
        bubbles: true,
        composed: true,
        detail: { productId: this.config.productId, variant, selectedOptions: [1, 2, 3].filter((index) => variant[`option${index}`]).map((index) => ({ name: this.one(`[data-option-position="${index}"]`)?.dataset.optionName || `Option ${index}`, value: variant[`option${index}`] })) },
      }));
    }
  }

  reconcileVariantVisibility() {
    if (this.isPreview) {
      const template = this.one('template[data-product-options-template]');
      const wrapper = this.one('[data-product-options]');
      if (template && wrapper) {
        [...wrapper.children].filter((node) => node.hasAttribute('data-product-option-group')).forEach((node) => node.remove());
        wrapper.prepend(template.content.cloneNode(true));
        show(wrapper, true);
      }
      return;
    }
    const visible = this.state.variants.filter((variant) => {
      const schedule = Date.parse(variant.schedule_visibility);
      return !(Number.isFinite(schedule) && Date.now() < schedule) && !(!variant.available && variant.hide_unavailable_option);
    });
    if (!visible.length) return;
    this.owned('[data-product-option-value]').forEach((element) => {
      const position = Number(element.dataset.optionPosition);
      const exists = visible.some((variant) => variant[`option${position}`] === element.dataset.productOptionValue);
      if (!exists) element.remove();
    });
    const currentVisible = visible.some((variant) => variant.id === this.state.current_variant_id);
    if (!currentVisible) this.applyVariant(visible[0]);
    const groups = this.owned('[data-product-option-group]');
    const onlyOne = this.state.variants.length > 1 && groups.length > 0
      && groups.every((group) => group.querySelectorAll('[data-product-option-value]').length <= 1);
    show(this.one('[data-product-options]'), !onlyOne);
  }

  syncOptions(variant) {
    this.owned('[data-variant-option]').forEach((control) => {
      const selected = variant[`option${control.dataset.optionPosition}`] || '';
      if (control.type === 'radio') control.checked = control.value === selected;
      else control.value = selected;
      const descriptions = [];
      if (control.checked && Number(control.dataset.optionPosition) === this.config.optionCount) {
        if (variant.variant_caption) descriptions.push(`pdp-variant-caption-${this.config.sectionId}-${this.config.productId}`);
        if (variant.pdp_subtitle_additional_info) descriptions.push(`pdp-variant-size-info-${this.config.sectionId}-${this.config.productId}`);
      }
      if (descriptions.length) control.setAttribute('aria-describedby', descriptions.join(' '));
      else control.removeAttribute('aria-describedby');
    });
    this.owned('template[data-product-selected-swatch-label-template]').forEach((template) => {
      const position = template.dataset.optionPosition;
      const selected = this.owned(`[data-variant-option][data-option-position="${position}"]`).find((input) => input.checked);
      const text = selected?.dataset.swatchLabel || '';
      let label = this.one(`[data-product-selected-swatch-label][data-option-position="${position}"]`);
      if (!text || this.config.showTextInsideSwatches) label?.remove();
      else {
        if (!label) {
          label = template.content.firstElementChild.cloneNode(true);
          template.before(label);
        }
        setText(label, text);
      }
    });
    const caption = this.ensureFragment('variant-caption', Boolean(variant.variant_caption));
    setText(caption, variant.variant_caption || '');
    const message = this.ensureFragment('variant-message', Boolean(variant.variant_message));
    setHtml(message?.querySelector('[data-product-variant-message-content]'), variant.variant_message || '');
    this.releasePendingControls();
  }

  releasePendingControls() {
    if (!this.initialized || !this.formControlsReady) return;
    const purchase = this.one('yise-purchase-options');
    if (purchase && !purchase.initialized) return;
    this.owned('[data-pending-product-control]').forEach((control) => {
      control.disabled = false;
      control.removeAttribute('data-pending-product-control');
    });
  }

  renderState() {
    const variant = this.currentVariant;
    if (!variant) return;
    const available = Boolean(variant.available);
    const waitlist = Boolean(this.access?.waitlistActive);
    this.syncCartFormVariant(variant);
    this.one('[data-product-info]')?.classList.toggle('pdp-variant-unavailable', !available || waitlist);
    const respectsWaitlist = this.dataset.buttonWaitlist !== 'false';
    this.owned('[data-product-add]').forEach((button) => {
      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
      show(button, available && (!respectsWaitlist || !waitlist));
    });
    this.owned('[data-product-add-label]').forEach((label) => setText(label, available ? label.dataset.availableLabel : label.dataset.unavailableLabel));
    this.owned('[data-product-price]').forEach((price) => {
      setHtml(price, this.state.evaluatedPrice);
      show(price, available && price.dataset.priceVisible !== 'false');
    });
    this.owned('[data-product-notify]').forEach((button) => show(button, !available && (!respectsWaitlist || (!waitlist && (this.config.context !== 'pdp' || this.access.oosEnableNotify)))));
    this.owned('[data-product-unavailable]').forEach((button) => show(button, !available && !waitlist && !this.access.oosEnableNotify));
    this.owned('[data-product-waitlist]').forEach((button) => {
      show(button, waitlist);
      setText(button, this.access.wlButtonText);
      if (button instanceof HTMLAnchorElement) {
        button.href = `/products/${this.config.productHandle}?variant=${variant.id}&waitlist_signup=true`;
        button.setAttribute('aria-label', `${this.access.wlButtonText} ${this.config.productTitle}`);
      }
    });
    this.owned('[data-product-unlock]').forEach((element) => {
      show(element, this.access.accessUnlocked);
      setText(element.querySelector('[data-product-unlock-text]'), this.access.unlockedText);
    });
    this.owned('[data-product-afterpay]').forEach((element) => setText(element, this.state.afterpayInstallment));
    this.owned('[data-product-free-shipping]').forEach((element) => show(element, this.state.showFreeShippingFlag));
    const purchase = this.one('yise-purchase-options');
    let subscriptionPrice = this.state.subscriptionPrice;
    const override = Number(this.dataset.subscriptionPriceOverride);
    if (override > 0 && !document.body.hasAttribute('data-exclude-current-variant')) {
      const price = '$' + (Math.round(variant.price_cents * (1 - override / 100)) / 100).toFixed(2).replace('.00', '');
      subscriptionPrice = `<span class="subscription-override-original">${subscriptionPrice || ''}</span><span class="subscription-override-price">${price}</span>`;
    }
    purchase?.syncPricing?.({
      baseVariantPrice: this.state.baseVariantPrice,
      subscriptionPrice,
      title: this.state.subscriptionTitleText,
      benefits: this.state.subscriptionBenefitItems,
      banner: this.state.subscriptionBannerText,
      hideFrequencyDiscount: this.state.hideFrequencyDiscount,
    });
    this.renderFollowers();
  }

  syncCartFormVariant(variant) {
    this.dataset.currentVariantId = String(variant.id);
    this.dataset.available = String(Boolean(variant.available));
    if (this.form) {
      this.form.dataset.available = String(Boolean(variant.available));
      this.form.dataset.currentVariantId = String(variant.id);
      this.form.dataset.cartLineSubtitle = this.state.cartLineSubtitle;
      this.form.dataset.productUrl = this.state.cartLineUrl;
      this.form.dataset.variantPrice = this.state.cartLineVariantPrice;
      this.form.classList.toggle('hide-rc-container', !variant.available);
      const input = this.form.querySelector('input[name="id"]');
      if (input) input.value = String(variant.id);
      const media = this.form.querySelector('template[data-cart-line-media]');
      const source = this.one(`template[data-variant-cart-line-media][data-variant-id="${CSS.escape(String(variant.id))}"]`);
      if (media && source && media.dataset.currentVariantId !== String(variant.id)) {
        if (media.innerHTML.trim() !== source.innerHTML.trim()) media.content.replaceChildren(source.content.cloneNode(true));
        media.dataset.currentVariantId = String(variant.id);
      }
    }
  }

  updateCartFormData() {
    const node = this.form?.querySelector('script.cart-form-product-data');
    const variant = this.currentVariant;
    if (!node || !variant) return;
    let data;
    try { data = JSON.parse(node.textContent); } catch { return; }
    const allocation = variant.sellingPlanAllocations?.find((plan) => plan.id === this.state.selectedSellingPlanId);
    const pricing = allocation ? this.state.resolveSubscriptionPricing(variant, allocation) : this.state.resolveVariantPricing(variant);
    Object.assign(data, {
      variant_id: Number(variant.id),
      price: typeof pricing.price === 'number' ? pricing.price : allocation ? allocation.priceCents : variant.price_cents,
      compare_at_price: pricing.compareAtPrice ?? null,
      temporary_sale_price: allocation ? null : variant.temporarySalePrice || null,
      variant_title: variant.title === 'Default Title' ? '' : variant.title,
      url: (data.url || `/products/${this.config.productHandle}`).split('?')[0] + `?variant=${variant.id}`,
    });
    if (variant.featured_image || this.config.productFeaturedImage) data.image = variant.featured_image || this.config.productFeaturedImage;
    const json = JSON.stringify(data);
    if (node.textContent !== json) node.textContent = json;
  }

  handlePurchaseChange(event) {
    if (event.target.closest('yise-product-form') !== this) return;
    const { selectedSellingPlanId, previousSellingPlanId } = event.detail;
    this.state.selectedSellingPlanId = selectedSellingPlanId || null;
    this.renderState();
    if (selectedSellingPlanId && previousSellingPlanId && selectedSellingPlanId !== previousSellingPlanId) {
      this.announceSellingPlanChange(selectedSellingPlanId);
    }
  }

  handlePlanInput(event) {
    if (!event.target.matches?.('input[name="selling_plan"]') || event.target.closest('yise-product-form') !== this) return;
    const value = event.target.value || null;
    if (this.state.selectedSellingPlanId === value) return;
    const previousValue = this.state.selectedSellingPlanId;
    this.state.selectedSellingPlanId = value;
    this.state.updatePriceForSellingPlan();
    this.updateCartTotal();
    this.updateExcludedVariantBodyAttribute();
    this.renderState();
    this.updateCartFormData();
    if (previousValue && value) this.announceSellingPlanChange(value);
  }

  announceSellingPlanChange(id) {
    const plan = this.currentVariant.sellingPlanAllocations.find((candidate) => candidate.id === id);
    if (!plan) return;
    const parts = [];
    const frequency = this.state.getFrequencyLabel(plan);
    if (frequency) parts.push(`Delivery every ${frequency}`);
    if (this.state.price) parts.push(`Price ${this.state.price}`);
    this.announce(parts.join('. '));
  }

  handleClick(event) {
    const target = event.target.closest?.('[data-product-notify], [data-product-waitlist], [data-product-points-trigger]');
    if (!target || target.closest('yise-product-form') !== this) return;
    if (target instanceof HTMLAnchorElement) return;
    if (target.hasAttribute('data-product-points-trigger')) {
      const expanded = target.getAttribute('aria-expanded') !== 'true';
      this.rewardsTooltipOpen = expanded;
      target.setAttribute('aria-expanded', String(expanded));
      show(this.one('[data-product-points-tooltip]'), expanded);
    } else {
      if (target.hasAttribute('data-product-waitlist')) this.dialogs.open('waitlist');
      else this.handleOutOfStock();
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape' || !event.target.closest?.('[data-product-points-trigger], [data-product-points-tooltip]')) return;
    event.preventDefault();
    event.stopPropagation();
    this.rewardsTooltipOpen = false;
    this.one('[data-product-points-trigger]')?.setAttribute('aria-expanded', 'false');
    show(this.one('[data-product-points-tooltip]'), false);
    this.one('[data-product-points-trigger]')?.focus();
  }

  announce(message) {
    const status = this.one('[data-product-variant-status]');
    if (!status) return;
    status.textContent = '';
    const state = this.state;
    queueMicrotask(() => {
      if (!this.isConnected || state !== this.state) return;
      this.one('[data-product-variant-availability]')?.remove();
      status.textContent = message;
    });
  }

  announceVariantChange(variant) {
    const text = (html) => {
      const holder = document.createElement('div');
      holder.innerHTML = html || '';
      return holder.textContent.replace(/\s+/g, ' ').trim();
    };
    const parts = [];
    if (variant.title && variant.title !== 'Default Title') parts.push(variant.title);
    if (this.state.price) parts.push(`Price ${this.state.price}`);
    parts.push(this.access.waitlistActive ? 'Waitlist available.' : variant.available ? 'Available.' : 'Unavailable.');
    for (const html of [variant.variant_message, variant.variant_caption]) {
      const value = text(html);
      if (value) parts.push(value);
    }
    this.announce(parts.join('. '));
  }

  updateExcludedVariantBodyAttribute() {
    const parent = this.parentElement?.closest('yise-product-form[data-template-product]');
    if (!this.formControlsReady && parent?.initialized) return;
    const excluded = (document.body.getAttribute('data-subscription-excluded-variants') || '').split('\n').map((id) => id.trim()).filter(Boolean);
    const isExcluded = excluded.includes(this.state.current_variant_id);
    if (isExcluded) document.body.setAttribute('data-exclude-current-variant', 'true');
    else document.body.removeAttribute('data-exclude-current-variant');
    document.querySelectorAll('yise-product-form[data-template-product]').forEach((owner) => {
      if (isExcluded) owner.setAttribute('data-exclude-current-variant', 'true');
      else owner.removeAttribute('data-exclude-current-variant');
    });
  }

  updateHistory(variant) {
    if (!variant) return;
    const path = `${location.protocol}//${location.host}${location.pathname}?variant=${variant.id}`;
    history.replaceState({ path }, '', path);
  }

  openIntentDialog() {
    if (!this.config.history) return;
    const query = this.initialQuery || new URLSearchParams(location.search);
    if (this.access.waitlistActive && (query.get('waitlist') === 'true' || query.get('waitlist_signup') === 'true')) this.dialogs.open('waitlist');
    else if (!this.access.waitlistActive && this.access.oosEnableNotify && (query.get('OutOfStock') === 'true' || query.get('waitlist') === 'true')) this.handleOutOfStock();
  }

  handleOutOfStock() {
    const variantQuery = this.state.current_variant_id ? `&variant=${this.state.current_variant_id}` : '';
    const path = `/products/${this.config.productHandle}?OutOfStock=true${variantQuery}`;
    if (location.pathname + location.search !== path) location.href = path;
    else this.dialogs.open('oos');
  }

  restoreBundleInitialHistory() {
    const id = this.dataset.bundleInitialSourceVariantId;
    delete this.dataset.bundleInitialSourceVariantId;
    if (!id || !this.config.defaultVariantId || this.initialQuery.has('variant')) return;
    const variant = this.state.variants.find((candidate) => String(candidate.id) === id);
    if (!variant) return;
    this.updateHistory(variant);
    if (this.owned('.media-gallery_swiper-container-wrapper').length > 1) this.syncMedia(variant);
  }

  syncMedia(variant) {
    if (!this.config.history) return;
    const galleries = this.owned('.media-gallery_swiper-container-wrapper');
    if (galleries.length > 1) {
      galleries.forEach((gallery) => {
        gallery.style.display = gallery.dataset.variantId === String(variant.id) ? '' : 'none';
        gallery.querySelectorAll('video').forEach((video) => video.pause());
      });
      this.galleryTimers ??= new Set();
      const signal = this.abortController?.signal;
      const timer = setTimeout(() => {
        this.galleryTimers.delete(timer);
        if (!this.isConnected || signal?.aborted) return;
        const gallery = galleries.find((candidate) => candidate.dataset.variantId === String(variant.id));
        gallery?.galleryTop?.update();
        gallery?.galleryThumbs?.update();
      }, 10);
      this.galleryTimers.add(timer);
    } else {
      const thumbs = this.owned('.media-gallery_gallery-thumbs .swiper-slide');
      const thumbnail = thumbs.find((element) => element.getAttribute('data-thumbnail-variants')?.includes(variant.id))
        || thumbs.find((element) => element.getAttribute('data-thumbnail-variants')?.includes('#featured'));
      thumbnail?.click();
    }
  }

  syncWishlist(variant) {
    if (!window.SwymPageData || !window._swat) return;
    window.SwymPageData.epi = Number(variant.id);
    const state = this.state;
    window._swat.fetchWrtEventTypeET((items) => {
      const wishlist = this.one('[data-product-wishlist]');
      if (!wishlist || !this.isConnected || this.state !== state) return;
      const added = items.some((item) => Number(item.epi) === Number(window.SwymPageData.epi));
      wishlist.setAttribute('aria-disabled', String(added));
      setText(wishlist, added ? 'Added To Wishlist' : 'Add To Wishlist');
      if (added) this.announce(`${this.config.productTitle || 'Item'} saved to wishlist.`);
    }, 4);
  }

  updateCartTotal() {
    if (window.Shopify?.cart) this.state.cartTotal = window.Shopify.cart.total_price / 100;
  }

  ensureFragment(name, visible) {
    const template = this.one(`template[data-product-${name}-template]`);
    let element = this.one(`[data-product-${name}]`);
    if (!visible) {
      element?.remove();
      return null;
    }
    if (!element && template) {
      element = template.content.firstElementChild?.cloneNode(true) || null;
      if (element) template.before(element);
    }
    return element;
  }

  renderFollowers() {
    const variant = this.currentVariant;
    const size = this.one('[data-product-variant-size]');
    const title = variant.title && variant.title !== 'Default Title' && !variant.title.includes('$') ? variant.title : '';
    const fallback = size?.dataset.defaultSize || '';
    this.ensureFragment('variant-size-bullet', Boolean(title || fallback));
    setHtml(size, title || fallback);
    const sizeInfo = this.ensureFragment('variant-size-info', Boolean(variant.pdp_subtitle_additional_info));
    setHtml(sizeInfo, variant.pdp_subtitle_additional_info);
    const value = this.ensureFragment('value', Boolean(this.state.showValueMessage));
    setHtml(value, `GET ${this.state.valueMessageAmount} VALUE FOR ${this.state.price.replace('.00', '')}`);
    const points = this.ensureFragment('points', Boolean(this.state.hasLoyaltyPoints));
    if (!points) return;
    const copyMode = this.state.earnPointsMessage ? 'custom' : 'default';
    const trigger = points.querySelector('[data-product-points-trigger]');
    if (points.dataset.pointsCopyMode !== copyMode) {
      while (points.firstChild && points.firstChild !== trigger) points.firstChild.remove();
      const copy = this.one(`template[data-product-points-${copyMode}-template]`);
      if (copy) trigger.before(copy.content.cloneNode(true));
      points.dataset.pointsCopyMode = copyMode;
    }
    setHtml(points.querySelector('[data-product-points-text="custom"]'), this.state.earnPointsMessage);
    setText(points.querySelector('[data-product-points-text="number"]'), this.state.rewardsPoints);
    const tooltip = points.querySelector('[data-product-points-tooltip]');
    setHtml(tooltip, this.state.loyaltyConfig.tooltipText);
    show(tooltip, Boolean(this.rewardsTooltipOpen));
    trigger?.setAttribute('aria-expanded', String(Boolean(this.rewardsTooltipOpen)));
  }
}

defineElement('yise-product-form', YiseProductForm);
